<template>
  <cx-toast-card :visible="visible" :closable="!submitted" @close="$emit('close')">
    <div ref="stage" class="csat-popup">
      <transition
        name="csat-swap"
        @before-leave="pinStageHeight"
        @enter="pinStageHeight"
        @after-enter="releaseStageHeight"
        @enter-cancelled="releaseStageHeight"
      >
        <div v-if="!submitted" key="form" class="csat-popup__panel csat-popup__form">
          <div :id="questionId" class="csat-popup__question">{{ title }}</div>
          <star-rating
            v-model="score"
            class="csat-popup__rating"
            :labelled-by="questionId"
            :worst-label="CSAT_RATING_WORST_LABEL"
            :best-label="CSAT_RATING_BEST_LABEL"
          />
          <textarea
            v-model="comment"
            class="csat-popup__comment"
            rows="1"
            :placeholder="CSAT_POPUP_COMMENT_PLACEHOLDER"
            :aria-label="CSAT_POPUP_COMMENT_PLACEHOLDER"
          />
          <div class="csat-popup__error" :role="error ? 'alert' : null">
            <template v-if="error">{{ CSAT_POPUP_ERROR_MESSAGE }}</template>
          </div>
          <riseup-button
            class="csat-popup__submit"
            :title="CSAT_SUBMIT_LABEL"
            :disabled="!score || loading"
            :loading="loading"
            @click="handleSubmit"
          />
        </div>
        <div v-else key="done" ref="done" class="csat-popup__panel csat-popup__done" role="status" tabindex="-1">
          <img
            class="csat-popup__done-badge"
            :src="require('@/assets/chat-bot/csat-confirmation-badge.svg')"
            width="45"
            height="46"
            alt=""
          />
          <div class="csat-popup__done-message">{{ CSAT_POPUP_CONFIRMATION_MESSAGE }}</div>
        </div>
      </transition>
    </div>
  </cx-toast-card>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import {
  CSAT_POPUP_CONFIRMATION_MESSAGE,
  CSAT_POPUP_TITLE,
  CSAT_POPUP_COMMENT_PLACEHOLDER,
  CSAT_POPUP_ERROR_MESSAGE,
  CSAT_RATING_BEST_LABEL,
  CSAT_RATING_WORST_LABEL,
  CSAT_SUBMIT_LABEL,
} from '@/constants/chat';
import StarRating from '@/base-components/StarRating.vue';
import CxToastCard from './CxToastCard.vue';

export default {
  name: 'ChatCsatPopup',
  components: {
    CxToastCard,
    StarRating,
    RiseupButton: BaseUI.RiseupButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: CSAT_POPUP_TITLE,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    submitted: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      score: 0,
      comment: '',
      questionId: `csat-popup-question-${this._uid}`,
      CSAT_POPUP_CONFIRMATION_MESSAGE,
      CSAT_POPUP_COMMENT_PLACEHOLDER,
      CSAT_POPUP_ERROR_MESSAGE,
      CSAT_RATING_BEST_LABEL,
      CSAT_RATING_WORST_LABEL,
      CSAT_SUBMIT_LABEL,
    };
  },
  watch: {
    visible(visible) {
      if (visible) {
        this.score = 0;
        this.comment = '';
      }
    },
    // The send button unmounts under the keyboard user's focus.
    submitted(submitted) {
      if (submitted) {
        this.$nextTick(() => this.$refs.done && this.$refs.done.focus());
      }
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('submit', { score: this.score, comment: this.comment.trim() || null });
    },
    // The leaving panel is out of flow, so the stage must be pinned to px for
    // its height to animate: the outgoing panel's before it leaves, the incoming
    // panel's as it enters; released to auto once the swap is over.
    pinStageHeight(el) {
      if (this.$refs.stage) {
        this.$refs.stage.style.height = `${el.offsetHeight}px`;
      }
    },
    releaseStageHeight() {
      if (this.$refs.stage) {
        this.$refs.stage.style.height = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/border-radius";
@import "~@riseupil/base-ui/src/scss/spacings";
@import "~@riseupil/base-ui/src/scss/typography";

$swap-height-duration: 0.4s;
$swap-fade-duration: 0.25s;
$swap-ease: cubic-bezier(0.4, 0, 0.2, 1);

.csat-popup {
  position: relative;
  width: 100%;
  overflow: hidden;
  direction: rtl;
  font-family: SimplerPro;
  color: $riseup_black;
  text-align: center;
  transition: height $swap-height-duration $swap-ease;
}

.csat-popup__panel {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
}

// Paddings and widths are the Figma frame's measurements (1406:1926); everything
// with a design token uses the shared variable.
.csat-popup__form {
  padding: 40px 31px 26px;
}

.csat-popup__question {
  width: 100%;
  font-size: $ri-font-size-16;
  line-height: 24px;
  margin-bottom: 13px;
}

.csat-popup__rating {
  width: 261px;
  max-width: 100%;
  margin-bottom: 27px;
}

// Not base-ui's TextArea: that is a labelled form field whose focus/filled
// border rules outrank scoped overrides, so the card would fight it.
.csat-popup__comment {
  width: 100%;
  height: 58px;
  box-sizing: border-box;
  padding: 18px 17px;
  border: 1px solid $riseup_gray_20;
  border-radius: $ri-border-radius-medium;
  font-family: inherit;
  font-size: $ri-font-size-14;
  line-height: 20px;
  color: $riseup_black;
  text-align: right;
  resize: none;
  outline: none;
  margin-bottom: $ri-spacing-sm;

  &:focus {
    border-color: $riseup_blue;
  }

  &::placeholder {
    color: rgba($riseup_black, 0.2);
  }
}

.csat-popup__error {
  height: 16px;
  margin-bottom: $ri-spacing-sm;
  font-size: $ri-font-size-12;
  line-height: 16px;
  color: $riseup_danger_red;
}

.csat-popup__submit.riseup-button {
  width: 100%;
  height: 50px;
  border-radius: $button-border-radius;
  font-size: $ri-font-size-16;
}

// Figma 1487:2090 measurements.
.csat-popup__done {
  padding: 29px 0;
  gap: $ri-spacing-s;
  outline: none;
}

.csat-popup__done-badge {
  display: block;
  width: 45px;
  height: 46px;
}

.csat-popup__done-message {
  font-size: $ri-font-size-16;
  line-height: 24px;
  font-weight: $ri-font-weight-bold;
}

.csat-swap-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transition: opacity $swap-fade-duration $swap-ease, transform $swap-fade-duration $swap-ease;
}

.csat-swap-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

.csat-swap-enter-active {
  transition: opacity $swap-fade-duration $swap-ease 0.15s, transform $swap-fade-duration $swap-ease 0.15s;

  .csat-popup__done-badge {
    transition: transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s;
  }
}

.csat-swap-enter {
  opacity: 0;
  transform: translateY(10px);

  .csat-popup__done-badge {
    transform: scale(0.6);
  }
}

@media (prefers-reduced-motion: reduce) {
  .csat-popup,
  .csat-swap-leave-active,
  .csat-swap-enter-active,
  .csat-swap-enter-active .csat-popup__done-badge {
    transition-duration: 0.01s;
    transition-delay: 0s;
  }
}
</style>
